import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IPlano, IPlanoCompletoMultiRange, IPlanoMultiRangeRequest, IPlanoRange, TipoPlano } from '../../models'

// ==================== ASYNC THUNKS ====================

export const setPlanoCliente = createAsyncThunk(
  'planoVeles/setPlanoCliente',
  async (payload: {
    IdCliente: number
    planoData: IPlanoCompletoMultiRange | null
  }) => {
    return payload
  }
)

export const setPlanoInserido = createAsyncThunk(
  'planoVeles/setPlanoInserido',
  async (payload: {
    dadosOriginais: IPlanoMultiRangeRequest
  }) => {
    return payload
  }
)

export const setPlanoAtualizado = createAsyncThunk(
  'planoVeles/setPlanoAtualizado',
  async (payload: {
    dadosOriginais: IPlanoMultiRangeRequest
  }) => {
    return payload
  }
)

// ==================== STATE INTERFACE ====================

interface PlanoVelesState {
  // Estados de UI
  loading: boolean
  error: string
  loadingObter: boolean
  dadosIniciaisCarregados: boolean

  // Dados principais (plano pode ser null quando cliente está inativo)
  plano: IPlanoCompletoMultiRange | null
  planoAtual: IPlanoCompletoMultiRange | null
  IdClienteAtual: number
  planoInserido: IPlano
  planoAtualizado: IPlano

  // Referências
  planoEmEdicao: IPlanoMultiRangeRequest
  ultimoPlanoValido: IPlanoMultiRangeRequest

  planoCarregadoInicial: IPlanoMultiRangeRequest | IPlanoCompletoMultiRange

  // Estados do formulário
  tipoPlano: IPlanoMultiRangeRequest['TipoPlano']
  quantidadeMinimaFee: number
  quantidadeMaximaFee: number
  valorFee: number
  quantidadePre: number
  valorPre: number
  valorUnitarioPre: number
  expiracaoPre: Date | null
  quantidadePOC: number
  expiracaoPOC: Date | null
  rangesPosPago: IPlanoRange[]
  rangesFeeExcedente: IPlanoRange[]
  camposComErro: Set<string>
}

// ==================== FUNÇÕES AUXILIARES ====================

const criarPlanoVazio = (): IPlanoCompletoMultiRange => ({
  data: null,
  IdCliente: 0,
  ClienteNome: '',
  ClienteDocumento: '',
  IdPlano: 0,
  TipoPlano: TipoPlano.FeeMensal,
  PlanoInclusao: new Date(),
  IdPlanoCliente: 0,
  PlanoClienteAtivo: false,
  PlanoClienteInclusao: new Date(),
  PlanoClienteAtualizacao: undefined,

  FeeQuantidadeMinima: undefined,
  FeeQuantidadeMaxima: undefined,
  FeeValorMensal: undefined,
  FeeInclusao: undefined,

  RangesFeeExcedente: [],
  FeeExcedenteQuantidadeMinima: undefined,
  FeeExcedenteQuantidadeMaxima: undefined,
  FeeExcedenteValorUnitario: undefined,
  FeeExcedenteInclusao: undefined,

  PosQuantidadeMinima: undefined,
  PosQuantidadeMaxima: undefined,
  PosValorUnitario: undefined,
  PosInclusao: undefined,
  RangesPosPago: [],

  PreQuantidade: undefined,
  PreValorTotal: undefined,
  PreValorUnitario: undefined,
  PreInclusao: undefined,
  PreExpiracao: new Date(),

  POCQuantidade: undefined,
  POCExpiracao: new Date(),
  POCInclusao: undefined
})

const criarPlanoSimplesVazio = (): IPlano => ({
  IdPlano: 0,
  Ativo: false,
  Inclusao: ''
})

export const criarPlanoRequestVazio = (): IPlanoMultiRangeRequest => ({
  IdCliente: 0,
  TipoPlano: TipoPlano.FeeMensal,

  // Campos opcionais
  QuantidadeMinimaPre: undefined,
  QuantidadeMaximaPre: undefined,
  ValorPre: undefined,
  ValorUnitarioPre: undefined,
  ExpiracaoPre: undefined,

  RangesPosPago: [],

  QuantidadeMinimaFee: 1,
  QuantidadeMaximaFee: 1,
  ValorFee: 0,
  QuantidadeMinimaExcedente: undefined,
  ValorUnitarioExcedente: undefined,

  QuantidadePOC: undefined,
  ExpiracaoPOC: undefined,

  RangesFeeExcedente: [{ min: 0, max: 999999999, valor: 0 }]
})

const resetFormulario = () => ({
  planoCarregadoInicial: criarPlanoRequestVazio(),
  tipoPlano: TipoPlano.FeeMensal,
  quantidadeMinimaFee: 1,
  quantidadeMaximaFee: 1,
  valorFee: 0,
  rangesFeeExcedente: [{ min: 0, max: 999999999, valor: 0 }],
  rangesPosPago: [],
  quantidadePre: 0,
  valorPre: 0,
  valorUnitarioPre: 0,
  expiracaoPre: null,
  quantidadePOC: 0,
  expiracaoPOC: null,
  camposComErro: new Set<string>()
})

// ==================== INITIAL STATE ====================

const initialState: PlanoVelesState = {
  // Estados de UI
  loading: false,
  error: '',
  loadingObter: false,
  dadosIniciaisCarregados: false,

  // Dados principais (plano inicia como null)
  plano: null,
  planoAtual: criarPlanoVazio(),
  IdClienteAtual: 0,
  planoInserido: criarPlanoSimplesVazio(),
  planoAtualizado: criarPlanoSimplesVazio(),

  // Referências
  planoEmEdicao: criarPlanoRequestVazio(),
  ultimoPlanoValido: criarPlanoRequestVazio(),

  // Estados do formulário
  ...resetFormulario()
}

// ==================== SLICE ====================

const planoVelesSlice = createSlice({
  name: 'planoVeles',
  initialState,
  reducers: {
    // ==================== AÇÕES SÍNCRONAS ====================

    // Limpar estados
    clearAll: () => initialState,

    clearError: (state) => {
      state.error = ''
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },

    setLoadingObter: (state, action: PayloadAction<boolean>) => {
      state.loadingObter = action.payload
    },

    setDadosIniciaisCarregados: (state, action: PayloadAction<boolean>) => {
      state.dadosIniciaisCarregados = action.payload
    },

    setPlanoEmEdicao: (state, action: PayloadAction<IPlanoMultiRangeRequest>) => {
      state.planoEmEdicao = action.payload
    },

    setUltimoPlanoValido: (state, action: PayloadAction<IPlanoMultiRangeRequest>) => {
      state.ultimoPlanoValido = action.payload
    },

    setCreateClienteFormPlanoState: (state, action: PayloadAction<{ plano: IPlanoMultiRangeRequest }>) => {
      state.planoEmEdicao = action.payload.plano
      state.ultimoPlanoValido = action.payload.plano
    },

    setPlanoCarregadoInicial: (state, action: PayloadAction<IPlanoMultiRangeRequest | IPlanoCompletoMultiRange>) => {
      state.planoCarregadoInicial = action.payload
    },

    setTipoPlano: (state, action: PayloadAction<IPlanoMultiRangeRequest['TipoPlano']>) => {
      state.tipoPlano = action.payload
    },

    setQuantidadeMinimaFee: (state, action: PayloadAction<number>) => {
      state.quantidadeMinimaFee = action.payload
    },

    setQuantidadeMaximaFee: (state, action: PayloadAction<number>) => {
      state.quantidadeMaximaFee = action.payload
    },

    setValorFee: (state, action: PayloadAction<number>) => {
      state.valorFee = action.payload
    },

    setQuantidadePre: (state, action: PayloadAction<number>) => {
      state.quantidadePre = action.payload
    },

    setValorPre: (state, action: PayloadAction<number>) => {
      state.valorPre = action.payload
    },

    setValorUnitarioPre: (state, action: PayloadAction<number>) => {
      state.valorUnitarioPre = action.payload
    },

    setExpiracaoPre: (state, action: PayloadAction<Date | null>) => {
      state.expiracaoPre = action.payload
    },

    setQuantidadePOC: (state, action: PayloadAction<number>) => {
      state.quantidadePOC = action.payload
    },

    setExpiracaoPOC: (state, action: PayloadAction<Date | null>) => {
      state.expiracaoPOC = action.payload
    },

    setRangesPosPago: (state, action: PayloadAction<IPlanoRange[]>) => {
      state.rangesPosPago = action.payload
    },

    setRangesFeeExcedente: (state, action: PayloadAction<IPlanoRange[]>) => {
      state.rangesFeeExcedente = action.payload
    },

    setCamposComErro: (state, action: PayloadAction<Set<string>>) => {
      state.camposComErro = action.payload
    },

    setPlano: (state, action: PayloadAction<IPlanoCompletoMultiRange | null>) => {
      state.plano = action.payload
    },

    resetFormularioPlanoState: (state) => {
      const formularioResetado = resetFormulario()
      Object.assign(state, formularioResetado)
    },

    resetPlanoState: (state) => {
      state.planoEmEdicao = criarPlanoRequestVazio()
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(setPlanoCliente.pending, (state) => {
        state.loading = true
        state.error = ''
      })
      .addCase(setPlanoCliente.fulfilled, (state, action) => {
        state.loading = false
        state.plano = action.payload.planoData
        state.planoAtual = action.payload.planoData
        state.IdClienteAtual = action.payload.IdCliente
      })
      .addCase(setPlanoCliente.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Erro ao carregar plano'
        state.plano = criarPlanoVazio()
        state.planoAtual = criarPlanoVazio()
      })
  }
})

// ==================== EXPORTS ====================

export const {
  clearAll,
  clearError,
  setError,
  setLoading,
  setPlanoEmEdicao,
  setUltimoPlanoValido,
  setCreateClienteFormPlanoState,
  resetPlanoState,
  setPlanoCarregadoInicial,
  setTipoPlano,
  setQuantidadeMinimaFee,
  setQuantidadeMaximaFee,
  setValorFee,
  setQuantidadePre,
  setValorPre,
  setValorUnitarioPre,
  setExpiracaoPre,
  setQuantidadePOC,
  setExpiracaoPOC,
  setRangesPosPago,
  setRangesFeeExcedente,
  setCamposComErro,
  setDadosIniciaisCarregados,
  setLoadingObter,
  resetFormularioPlanoState,
  setPlano
} = planoVelesSlice.actions

export default planoVelesSlice.reducer